import { api, apiClient } from "../lib";
import {
  getResponseDownloadFilename,
  triggerBlobDownload,
} from "../utils/downloadFilename";
import type { DocumentViewResponse } from "../types/reportTemplate";
import type { ReportComposition } from "../utils/reportComposition";

export interface SignOffValidationResult {
  isValid: boolean;
  blockingErrors: string[];
  warnings: string[];
  specimenStatus: Array<{
    specimenId: string;
    specimenLabel: string;
    hasDiagnosis: boolean;
    hasCategory: boolean;
    hasCptCode: boolean;
    hasIcdCode: boolean;
  }>;
}

export interface ReportPreviewData {
  caseNumber: string;
  patient: {
    name: string;
    dob: string;
    mrn?: string;
  };
  physician: {
    name: string;
    practice?: string;
  };
  facility: string;
  sendingPracticeId?: string;
  surgeryCenterLabId?: string;
  dateCollected: string;
  dateReceived: string;
  procedure?: string;
  statusName?: string;
  clinicalHistoryNotes?: string;
  frozenInterpretation?: string;
  indications?: string;
  impressions?: string;
  specimens?: Array<{
    id: string;
    number: string;
    sourceName: string;
    collectionMethod?: string | null;
    findings?: string | null;
    organMapTemplateId: string | null;
    organMapTemplateName: string | null;
    organMapImageUrl: string | null;
    organMapWidth: number | null;
    organMapHeight: number | null;
    subsiteName: string | null;
    subsitePositionX: number | null;
    subsitePositionY: number | null;
  }>;
  diagnoses: Array<{
    specimenLabel: string;
    source: string;
    procedure?: string;
    findings?: string;
    diagnosisText: string;
    microscopicDescription?: string;
    grossDescription?: string;
    category?: string;
    cptCode?: string;
    icdCode?: string;
  }>;
  images?: Array<{
    id: string;
    imageUrl: string;
    thumbnailUrl?: string;
    caption?: string;
    magnification?: string;
    slideId?: string;
  }>;
  organMaps?: Array<{
    id: string;
    name: string;
    organName: string;
    svgContent?: string;
    imageUrl?: string;
  }>;
  signatureBlock: {
    isSigned: boolean;
    signedBy?: string;
    signedAt?: string;
  };
  templateId?: string;
  templateName?: string;
  // C-818 Section 7: Report Integration - Additional stain results
  ihcResults?: Array<{
    stainName: string;
    stainCode: string;
    result: string;
  }>;
  specialStainResults?: Array<{
    stainName: string;
    stainCode: string;
    result: string;
  }>;
  referenceLabResults?: Array<{
    testName: string;
    labName: string;
    referenceNumber?: string;
    result: string;
    interpretation?: string;
  }>;
  review?: IssuedReportReview;
}

export type ReportReviewErrorCode =
  | "REPORT_CONTENT_CHANGED"
  | "REPORT_REVIEW_REQUIRED"
  | "REPORT_REVIEW_STALE";

export interface ReportReviewStatus {
  reviewRequired: boolean;
  actionLabel: "Sign Out" | "Preview & Sign Out";
  prefixId: string | null;
  pathologistUserId: string;
  failClosedReason?: "PREFIX_UNRESOLVED" | "PATHOLOGIST_INELIGIBLE";
}

export interface ReportContentVersion {
  reportComposition?: ReportComposition;
  fingerprint: string;
  reportContentRevision: number;
  templateId: string | null;
  rendererSchemaVersion: string;
  renderDate: string;
}

export interface IssuedReportReview {
  reviewRequired: boolean;
  contentVersion: ReportContentVersion;
  signatureSimulation: {
    signerName: string;
    credentials: string | null;
    fixedFootprint: true;
  };
}

export interface ReportPreviewEvidence {
  previewId: string;
  contentVersion: ReportContentVersion;
  reportData?: DocumentViewResponse;
}

interface ReportReviewHttpError {
  response?: {
    status?: number;
    data?: { code?: string; error?: string; message?: string };
  };
}

export class ReportContentChangedError extends Error {
  constructor() {
    super("The report changed; review the updated version before signing out.");
    this.name = "ReportContentChangedError";
  }
}

/** Required discussion trigger info per C-813 Section 7 */
export interface RequiredDiscussionTrigger {
  category: string;
  reason: string;
  diagnosisCategoryName?: string;
  diagnosisText?: string;
}

/** Required discussion info returned from sign-off validation */
export interface RequiredDiscussionInfo {
  hasRequiredTriggers: boolean;
  triggers: RequiredDiscussionTrigger[];
  missingDiscussions: RequiredDiscussionTrigger[];
  existingDiscussions: Array<{
    discussionId: string;
    category: string;
    status: string;
    isPending: boolean;
  }>;
}

export interface SignOffResult {
  success: boolean;
  signOffId?: string;
  signedAt?: string;
  signedBy?: { id: string; name: string };
  reportVersion?: number;
  deliveryQueue?: Array<{
    id: string;
    recipientType: string;
    recipientName: string;
    deliveryMethod: string;
    destination: string;
    scheduledAt: string;
    status: string;
  }>;
  sessionId?: string;
  sessionExpiresAt?: string;
  message?: string;
  blockingErrors?: string[];
  warnings?: string[];
  // Partial sign-off fields
  isPartialSignOff?: boolean;
  signedSpecimenIds?: string[];
  pendingSpecimenIds?: string[];
  /** Required discussion info per C-813 Section 7 */
  requiredDiscussions?: RequiredDiscussionInfo;
  reviewErrorCode?: ReportReviewErrorCode;
}

export interface SignOffSession {
  sessionValid: boolean;
  sessionId?: string;
  expiresAt?: string;
  message?: string;
}

export const REPORT_PDF_REQUEST_TIMEOUT_MS = 90000;

export interface SignOffHistoryItem {
  id: string;
  caseId: string;
  signedById: string;
  signedBy?: {
    id: string;
    fullName: string;
    email: string;
  };
  signedAt: string;
  signOffType: "initial" | "amended" | "amendment" | "addendum" | "corrected";
  reportVersion: number;
  diagnosisSnapshot?: string;
  amendmentReason?: string;
  addendumName?: string | null;
  comments?: string;
}

export interface DeliveryQueueItem {
  id: string;
  caseId: string;
  practiceId?: string;
  physicianId?: string;
  deliveryMethodId: string;
  recipientEmail?: string;
  recipientFax?: string;
  status: string;
  scheduledAt?: string;
  sentAt?: string;
  deliveryAttempts: number;
  lastError?: string;
  deliveryMethod?: {
    id: string;
    name: string;
  };
  practice?: {
    id: string;
    name: string;
  };
  physician?: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

/**
 * Validate a case for sign-off
 */
export function validateForSignOff(
  caseId: string
): Promise<SignOffValidationResult> {
  return api.post<SignOffValidationResult>(
    `/cases/${caseId}/sign-off/validate`
  );
}

/**
 * Get report preview data.
 * Optionally pass a version number to retrieve a historical snapshot.
 */
export function getReportPreview(
  caseId: string,
  version?: number,
  templateId?: string
): Promise<ReportPreviewData> {
  const query = new URLSearchParams();
  if (version !== undefined) query.set("version", String(version));
  if (templateId) query.set("templateId", templateId);
  const queryString = query.toString();
  const params = queryString ? `?${queryString}` : "";
  return api.get<ReportPreviewData>(
    `/cases/${caseId}/sign-off/preview${params}`
  );
}

export function issueProjectedReportPreview(
  caseId: string,
  reportData: DocumentViewResponse,
  templateId?: string
): Promise<ReportPreviewData> {
  return api.post<ReportPreviewData>(`/cases/${caseId}/sign-off/preview`, {
    templateId,
    reportData,
  });
}

export function getReportReviewStatus(
  caseId: string
): Promise<ReportReviewStatus> {
  return api.get<ReportReviewStatus>(`/cases/${caseId}/sign-off/review-status`);
}

export async function acknowledgeReportPreview(
  caseId: string,
  contentVersion: ReportContentVersion,
  reportData: DocumentViewResponse
): Promise<ReportPreviewEvidence> {
  try {
    const evidence = await api.post<ReportPreviewEvidence>(
      `/cases/${caseId}/sign-off/preview-review`,
      { contentVersion, reportData }
    );
    return { ...evidence, reportData };
  } catch (error) {
    const response = (error as ReportReviewHttpError).response;
    if (
      response?.status === 409 &&
      response.data?.code === "REPORT_CONTENT_CHANGED"
    ) {
      throw new ReportContentChangedError();
    }
    if (!response) {
      throw new Error(
        "The report review could not be recorded. Check your connection and try again."
      );
    }
    if (response.status === 401) {
      throw new Error(
        "Your session expired. Sign in again, then review the report once more."
      );
    }
    if (response.status === 403) {
      throw new Error(
        "You do not have permission to record this report review."
      );
    }
    throw new Error(
      "The server could not record the report review. Try again."
    );
  }
}

/**
 * Sign out a case (full or partial)
 * Handles 401 (auth required) and 422 (validation errors) gracefully
 * @param specimenIds - Optional: only sign off these specimens (partial sign-off)
 */
export async function signOutCase(
  caseId: string,
  options: {
    password?: string;
    sessionId?: string;
    skipWarnings?: boolean;
    specimenIds?: string[]; // For partial sign-off
    skipDiscussionChecks?: boolean;
    createRequiredDiscussions?: boolean;
    /** Template ID the pathologist had selected when signing off. Persisted on
     *  CaseSignOffHistory so the backend renderer reproduces the exact layout. */
    templateId?: string;
    previewEvidence?: ReportPreviewEvidence;
    reportData?: DocumentViewResponse;
    reviewedReflexProposalKeys?: string[];
    reviewedTissueCypherProposalKeys?: string[];
  } = {}
): Promise<SignOffResult> {
  try {
    const response = await apiClient.post<SignOffResult>(
      `/cases/${caseId}/sign-off`,
      options
    );
    return response.data;
  } catch (error: any) {
    const status = error.response?.status;
    const responseData = error.response?.data;

    // Handle 401 (authentication required / invalid password)
    const isAuthError = status === 401 || responseData?.requiresPassword;

    if (isAuthError) {
      return {
        success: false,
        requiresPassword: true,
        message: responseData?.message || "Please enter your password",
        ...responseData,
      } as SignOffResult & { requiresPassword?: boolean };
    }

    // Handle 422 (validation errors) - return blocking errors/warnings
    // Include session info if present (auth passed, sign-off failed)
    if (status === 422 || responseData?.blockingErrors) {
      return {
        success: false,
        blockingErrors: responseData?.blockingErrors || [],
        warnings: responseData?.warnings || [],
        message: responseData?.error || "Validation failed",
        // Include session info so it can be saved even on error
        sessionId: responseData?.sessionId,
        sessionExpiresAt: responseData?.sessionExpiresAt,
        // Include required discussion info per C-813 Section 7
        requiredDiscussions: responseData?.requiredDiscussions,
        reviewErrorCode: responseData?.code,
      };
    }

    // For other errors, throw with a user-friendly message
    throw new Error("Sign-out could not be completed. Please try again.");
  }
}

export interface AmendmentResult {
  success: boolean;
  amendmentId?: string;
  reportVersion?: number;
  message?: string;
  requiresPassword?: boolean;
  sessionId?: string;
  sessionExpiresAt?: string;
}

/**
 * Create an amendment.
 * Handles 401 (authentication required) gracefully so the caller can prompt
 * for a password instead of redirecting to login.
 */
export async function createAmendment(
  caseId: string,
  data: {
    type: "corrected" | "addendum" | "amended";
    reason: string;
    content?: string;
    password?: string;
    sessionId?: string;
    demographicChanges?: {
      patientFirstName?: string;
      patientLastName?: string;
      dateOfBirth?: string;
      dateCollected?: string;
    };
  }
): Promise<AmendmentResult> {
  try {
    const response = await apiClient.post<AmendmentResult>(
      `/cases/${caseId}/amendment`,
      data
    );
    return response.data;
  } catch (error: any) {
    const status = error.response?.status;
    const responseData = error.response?.data;

    const isAuthError =
      status === 401 ||
      responseData?.requiresPassword ||
      (error.message || "").toLowerCase().includes("authentication");

    if (isAuthError) {
      return {
        success: false,
        requiresPassword: true,
        message:
          responseData?.message ||
          error.message ||
          "Please enter your password",
        ...responseData,
      };
    }

    throw new Error(
      responseData?.error ||
        responseData?.message ||
        error.message ||
        "Failed to create amendment"
    );
  }
}

/**
 * Get sign-off history
 */
export function getSignOffHistory(
  caseId: string
): Promise<SignOffHistoryItem[]> {
  return api.get<SignOffHistoryItem[]>(`/cases/${caseId}/sign-off/history`);
}

/**
 * Get delivery queue for a case
 */
export function getDeliveryQueue(caseId: string): Promise<DeliveryQueueItem[]> {
  return api.get<DeliveryQueueItem[]>(`/cases/${caseId}/delivery-queue`);
}

/**
 * Cancel a pending delivery
 */
export function cancelDelivery(
  caseId: string,
  deliveryId: string
): Promise<{ success: boolean; message?: string }> {
  return api.post<{ success: boolean; message?: string }>(
    `/cases/${caseId}/delivery-queue/${deliveryId}/cancel`
  );
}

/**
 * Retry a failed delivery
 */
export function retryDelivery(
  caseId: string,
  deliveryId: string
): Promise<{ success: boolean; message?: string }> {
  return api.post<{ success: boolean; message?: string }>(
    `/cases/${caseId}/delivery-queue/${deliveryId}/retry`
  );
}

/**
 * Resend a delivery regardless of current status (including already delivered).
 * For portal deliveries, this revokes the old token and generates a new one.
 */
export function resendDelivery(
  caseId: string,
  deliveryId: string
): Promise<{ success: boolean; message?: string }> {
  return api.post<{ success: boolean; message?: string }>(
    `/cases/${caseId}/delivery-queue/${deliveryId}/resend`
  );
}

/**
 * Revoke a portal access token
 */
export function revokePortalToken(
  tokenId: string
): Promise<{ success: boolean; message?: string }> {
  return api.post<{ success: boolean; message?: string }>(
    `/report/portal/tokens/${tokenId}/revoke`
  );
}

/**
 * Restore (un-revoke) a portal access token
 */
export function restorePortalToken(
  tokenId: string
): Promise<{ success: boolean; message?: string }> {
  return api.post<{ success: boolean; message?: string }>(
    `/report/portal/tokens/${tokenId}/restore`
  );
}

/**
 * Get portal tokens for a delivery queue item
 */
export function getPortalTokensByDelivery(deliveryQueueId: string): Promise<
  Array<{
    id: string;
    caseId: string;
    recipientEmail: string;
    recipientName: string | null;
    accessedAt: string | null;
    revokedAt: string | null;
    createdAt: string;
  }>
> {
  return api.get(`/report/portal/tokens/by-delivery/${deliveryQueueId}`);
}

/**
 * Verify or create a sign-off session
 */
export function verifySignOffSession(
  password?: string,
  sessionId?: string
): Promise<SignOffSession> {
  return api.post<SignOffSession>(`/cases/sign-off/verify-session`, {
    password,
    sessionId,
  });
}

/**
 * Download the immutable signed report through the centralized renderer/cache.
 */
export async function downloadReportPdf(
  caseId: string,
  _caseNumber?: string,
  options?: { lastSigned?: boolean }
): Promise<void> {
  void _caseNumber; // Kept for caller compatibility; the backend header owns the canonical name.
  const response = await apiClient.get(
    `/cases/${caseId}/report/pdf${
      options?.lastSigned ? "?lastSigned=true" : ""
    }`,
    {
      responseType: "blob",
      timeout: REPORT_PDF_REQUEST_TIMEOUT_MS,
    }
  );
  const filename = getResponseDownloadFilename(
    response.headers["content-disposition"],
    "report.pdf"
  );
  triggerBlobDownload(
    new Blob([response.data as BlobPart], { type: "application/pdf" }),
    filename
  );
}

/**
 * Generate a PDF from the given HTML via Puppeteer and return a blob URL
 * pointing to the result. The caller is responsible for revoking the URL
 * with URL.revokeObjectURL() when it is no longer needed.
 */
export async function generatePdfBlobUrl(
  caseId: string,
  html: string
): Promise<string> {
  const response = await apiClient.post(
    `/cases/${caseId}/report/pdf`,
    { html },
    {
      responseType: "blob",
      timeout: REPORT_PDF_REQUEST_TIMEOUT_MS,
    }
  );
  const blob = new Blob([response.data as BlobPart], {
    type: "application/pdf",
  });
  return URL.createObjectURL(blob);
}

/**
 * Store a rendered HTML snapshot of the report at sign-off time.
 * The delivery queue uses this snapshot to generate PDFs for email/fax delivery
 * without requiring the frontend renderer.
 *
 * @param caseId - Case ID
 * @param html - Complete HTML document string built by buildReportHtml()
 */
export async function storeReportSnapshot(
  caseId: string,
  html: string
): Promise<void> {
  await apiClient.post(`/cases/${caseId}/report/snapshot`, { html });
}

// ==================== SEND REPORT (MANUAL DELIVERY) ====================

export interface CaseDeliveryMethodConfig {
  id: string;
  deliveryMethodId: string;
  deliveryMethodName: string;
  recipientType: "physician" | "practice" | "surgery_center" | "lab";
  recipientName: string;
  destination: string | null;
  email: string | null;
  fax: string | null;
  address: string | null;
}

export interface DeliveryAdmin {
  id: string;
  fullName: string;
  email: string;
}

export interface QueuedDeliveryItem {
  id: string;
  recipientName: string;
  deliveryMethod: string;
  status: string;
}

export function getActiveDeliveryMethodsForCase(
  caseId: string
): Promise<CaseDeliveryMethodConfig[]> {
  return api.get<CaseDeliveryMethodConfig[]>(
    `/cases/${caseId}/delivery-methods`
  );
}

export function queueManualDelivery(
  caseId: string,
  deliveryConfigIds: string[]
): Promise<{ queued: QueuedDeliveryItem[] }> {
  return api.post<{ queued: QueuedDeliveryItem[] }>(
    `/cases/${caseId}/delivery-queue/manual`,
    { deliveryConfigIds }
  );
}

export function getDeliveryAdmins(): Promise<DeliveryAdmin[]> {
  return api.get<DeliveryAdmin[]>("/cases/delivery-admins");
}
